import { z } from "zod";

/**
 * The House Rules editor — PRD §5.2, FR-2.4, FR-2.5, FR-8.4, FR-8.5.
 *
 * Every path through this surface produces a reviewable diff and NOTHING is
 * written until an explicit accept. That is one requirement, and it shows up
 * here as a shape: `propose` returns a proposal id and a diff, `accept` takes
 * a proposal id. There is no endpoint that takes markdown and saves it.
 */

/**
 * Deliberately larger than the 8,000-character cap (FR-2.4): an over-cap
 * document is a `422 RULES_TOO_LARGE` carrying the offending size (E-51), not
 * a 400 that tells the recipient nothing about how far over they are.
 */
export const RULES_BODY_LIMIT = 40_000;

/**
 * Exactly one of `instruction` (conversational — the model writes the new
 * document) or `markdown` (manual — the body IS the new document).
 */
export const RuleProposeRequest = z
  .object({
    instruction: z.string().trim().min(1).max(500).optional(),
    markdown: z.string().min(1).max(RULES_BODY_LIMIT).optional(),
  })
  .refine((v) => (v.instruction === undefined) !== (v.markdown === undefined), {
    message: "exactly one of instruction or markdown",
    path: ["instruction"],
  });
export type RuleProposeRequest = z.infer<typeof RuleProposeRequest>;

/**
 * `edited_markdown` is the [Edit] arm of `[Add] [Edit] [Dismiss]` (FR-8.3):
 * the recipient accepted the idea and rewrote the wording, which is a different
 * event from a plain accept and is recorded as `edited`.
 */
export const RuleAcceptRequest = z.object({
  proposal_id: z.uuid(),
  edited_markdown: z.string().min(1).max(RULES_BODY_LIMIT).optional(),
});
export type RuleAcceptRequest = z.infer<typeof RuleAcceptRequest>;

/** FR-2.5: rolling back is itself a new version. Nothing is ever unwritten. */
export const RuleRollbackRequest = z.object({ to_version: z.number().int().positive() });
export type RuleRollbackRequest = z.infer<typeof RuleRollbackRequest>;

export const DiffLine = z.object({
  type: z.enum(["same", "add", "del"]),
  line: z.string(),
});
export type DiffLine = z.infer<typeof DiffLine>;

/**
 * A pair of lines that may pull in opposite directions (FR-8, ruling
 * R-0905-6). ADVISORY: the client shows the pairs above the diff and a plain
 * Accept stays available — there is no resolution to choose and no gate.
 */
export const RuleConflict = z.object({
  existing_line: z.string(),
  proposed_line: z.string(),
});
export type RuleConflict = z.infer<typeof RuleConflict>;

/**
 * WHAT THIS DOCUMENT CANNOT DO (ruling R-0910-4), computed at propose time and
 * shown above Accept. Advisory, like `RuleConflict`: accepting with warnings is
 * allowed, and the desk keeps one notice line saying which ones stood.
 *
 * `door_open` and `door_closed` come from Nob's own reading of the document
 * (ruling R-0910-6), not from a pattern: a door that lets everyone through and
 * one that lets nobody through are both things the server then honours, so both
 * are said before Accept rather than discovered at the first knock.
 * `door_strict` (wave 80) is the third the reading drives, for the same reason:
 * a strict door holds a knock on one excuse, and the owner is told so, with
 * the sentence of theirs that made it strict.
 */
export const RuleWarning = z.object({
  code: z.enum([
    "no_gets_through",
    "no_receipts",
    "no_sections",
    "door_open",
    "door_closed",
    "door_strict",
  ]),
  text: z.string(),
});
export type RuleWarning = z.infer<typeof RuleWarning>;

export const RuleProposeResponse = z.object({
  proposal_id: z.uuid(),
  /** Unified-style text, as stored in `rule_proposals.diff_preview`. */
  diff: z.string(),
  /** The same edit, structured, for a side-by-side renderer. */
  diff_lines: z.array(DiffLine),
  resulting_markdown: z.string(),
  conflicts: z.array(RuleConflict),
  warnings: z.array(RuleWarning),
  /** Always true, and always stated: this response wrote nothing. */
  saves_on_accept: z.literal(true),
});
export type RuleProposeResponse = z.infer<typeof RuleProposeResponse>;

export const RulesVersionSummary = z.object({
  version: z.number().int().positive(),
  author: z.enum(["onboarding_short", "recipient", "nob_proposal", "rollback"]),
  change_note: z.string().nullable(),
  created_at: z.string(),
  chars: z.number().int().nonnegative(),
});
export type RulesVersionSummary = z.infer<typeof RulesVersionSummary>;

export const RulesVersionsResponse = z.object({ versions: z.array(RulesVersionSummary) });
export type RulesVersionsResponse = z.infer<typeof RulesVersionsResponse>;

export const RulesVersionResponse = z.object({
  version: z.number().int().positive(),
  markdown: z.string(),
});
export type RulesVersionResponse = z.infer<typeof RulesVersionResponse>;

/** What accept and rollback both return: the version that now stands. */
export const RulesSavedResponse = z.object({
  version: z.number().int().positive(),
  markdown: z.string(),
  change_note: z.string().nullable(),
});
export type RulesSavedResponse = z.infer<typeof RulesSavedResponse>;

/**
 * The feedback → House Rules proposal queue — PRD §5.2 `GET /rules/proposals`,
 * FR-8.3. Each item is a proposal the learning loop generated (or an explicit
 * edit not yet resolved), surfaced for the recipient to accept, edit or dismiss.
 * As with `propose`, `conflicts` is advisory (R-0905-6): shown above the diff,
 * never a gate on Accept.
 */
export const RuleProposalTriggerKind = z.enum([
  "explicit",
  "action_pattern",
  "insight",
  "consolidation",
  "contradiction",
]);
export type RuleProposalTriggerKind = z.infer<typeof RuleProposalTriggerKind>;

export const RuleProposalStatus = z.enum([
  "queued",
  "surfaced",
  "accepted",
  "edited",
  "dismissed",
  "superseded",
]);
export type RuleProposalStatus = z.infer<typeof RuleProposalStatus>;

export const RuleProposalQueueItem = z.object({
  id: z.uuid(),
  /** Where the proposal came from — `action_pattern`/`consolidation` are FR-8. */
  trigger_kind: RuleProposalTriggerKind,
  /** How many consistent signals produced it (1 for an explicit edit). */
  signal_count: z.number().int().nonnegative(),
  proposed_markdown: z.string(),
  diff_preview: z.string().nullable(),
  conflicts: z.array(RuleConflict),
  /** One-line human summary of the evidence, e.g. the change note. */
  evidence_summary: z.string(),
  created_at: z.string(),
  status: RuleProposalStatus,
});
export type RuleProposalQueueItem = z.infer<typeof RuleProposalQueueItem>;

export const RuleProposalsResponse = z.object({ proposals: z.array(RuleProposalQueueItem) });
export type RuleProposalsResponse = z.infer<typeof RuleProposalsResponse>;

/** `POST /rules/proposals/:id/dismiss`. */
export const RuleProposalDismissResponse = z.object({ dismissed: z.literal(true) });
export type RuleProposalDismissResponse = z.infer<typeof RuleProposalDismissResponse>;

/**
 * THE ONE SENTENCE THAT OPENS THE HOUSE RULES CONVERSATION (ruling R-0915-1).
 *
 * Here, in the one package both tiers import, because BOTH tiers match on the
 * exact words. The console sends it — the desk's "Personalize your house rules"
 * button and the chip that says the same thing — and the api READS it: this is
 * the message that opens an onboarding conversation, and the run derived from
 * the thread begins at the most recent copy of it (`doorman/onboarding.ts`).
 *
 * A copy on each side would drift, and the drift would be silent: the console
 * would send a sentence the server no longer recognises, and onboarding would
 * quietly stop opening. One string, one meaning, one place.
 *
 * IT IS WHAT A PERSON WOULD SAY. The old ask described a mechanism — "one
 * section at a time, propose what you'd change, and I'll accept, adjust or
 * skip each one" — because the walkthrough WAS a mechanism, and it read as a
 * checklist rather than as an AI. There is no section order to describe now,
 * so the sentence says the thing the owner actually wants and lets Nob decide
 * how to get there.
 */
export const ONBOARDING_ASK = "Let's set up my house rules.";
